import React, { useCallback, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
  alpha,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { AuxCategory, AuxFile } from '../../services/projectsService';
import { FileCategoryChip } from '../FileCategoryChip';

const NAUTILUS = { deepOcean: '#0f2134', sand: '#ccba9b' };
const MAX_FILE_BYTES = 50 * 1024 * 1024;

export interface AuxFileDraft {
  tempId: string;
  file: File;
  category: AuxCategory;
  description: string;
  sizeError?: string;
}

export interface AuxiliaryFilesWidgetProps {
  newEntries: AuxFileDraft[];
  onNewEntriesChange: (entries: AuxFileDraft[]) => void;
  existingFiles?: AuxFile[];
  onDeleteExisting?: (fileId: number) => void | Promise<void>;
  onDownloadExisting?: (file: AuxFile) => void | Promise<void>;
  loadingExisting?: boolean;
  disabled?: boolean;
}

const CATEGORY_LABELS: Record<AuxCategory, string> = {
  attachment: 'Attachments',
  archive: 'Internal Archives',
  crs: 'CRS',
};

const newTempId = () => `aux-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const AuxiliaryFilesWidget: React.FC<AuxiliaryFilesWidgetProps> = ({
  newEntries,
  onNewEntriesChange,
  existingFiles = [],
  onDeleteExisting,
  onDownloadExisting,
  loadingExisting = false,
  disabled = false,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const panelBorder = isDark ? '#2c3e50' : '#e0e0e0';
  const consoleColors = {
    accent: NAUTILUS.sand,
    borderStrong: isDark ? alpha(NAUTILUS.sand, 0.45) : alpha(NAUTILUS.deepOcean, 0.25),
    textSecondary: isDark ? '#b0bec5' : '#546e7a',
  };

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);

  const addFiles = useCallback(
    (files: File[], defaultCategory: AuxCategory = 'attachment') => {
      const next: AuxFileDraft[] = [];
      for (const file of files) {
        let sizeError: string | undefined;
        if (file.size > MAX_FILE_BYTES) {
          sizeError = 'Exceeds 50MB limit';
        }
        next.push({
          tempId: newTempId(),
          file,
          category: defaultCategory,
          description: '',
          sizeError,
        });
      }
      if (next.length > 0) {
        onNewEntriesChange([...newEntries, ...next]);
      }
    },
    [newEntries, onNewEntriesChange]
  );

  const handleDrag = (e: React.DragEvent, active: boolean) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    setDragActive(active);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (disabled) return;
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length > 0) {
      addFiles(files);
    }
  };

  const updateDraft = (tempId: string, patch: Partial<AuxFileDraft>) => {
    onNewEntriesChange(
      newEntries.map((entry) => (entry.tempId === tempId ? { ...entry, ...patch } : entry))
    );
  };

  const removeDraft = (tempId: string) => {
    onNewEntriesChange(newEntries.filter((entry) => entry.tempId !== tempId));
  };

  const hasRows = existingFiles.length > 0 || newEntries.length > 0;

  return (
    <Box sx={{ mt: 2 }}>
      <Typography
        sx={{
          fontSize: '0.65rem',
          fontWeight: 600,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: consoleColors.textSecondary,
          mb: 1,
        }}
      >
        Auxiliary Files
      </Typography>

      <Paper
        sx={{
          p: 2,
          mb: 2,
          border: '2px dashed',
          borderColor: dragActive ? consoleColors.accent : consoleColors.borderStrong,
          backgroundColor: dragActive
            ? alpha(consoleColors.accent, 0.08)
            : isDark
              ? alpha('#0f2134', 0.4)
              : alpha('#0f2134', 0.02),
          textAlign: 'center',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1,
          transition: 'all 0.2s ease',
          '&:hover': disabled
            ? {}
            : {
                borderColor: consoleColors.accent,
                bgcolor: alpha(consoleColors.accent, 0.04),
              },
        }}
        onDragEnter={(e) => handleDrag(e, true)}
        onDragLeave={(e) => handleDrag(e, false)}
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setDragActive(true);
        }}
        onDrop={handleDrop}
        onClick={() => {
          if (!disabled) fileInputRef.current?.click();
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          hidden
          disabled={disabled}
          onChange={(e) => {
            const files = Array.from(e.target.files || []);
            if (files.length > 0) addFiles(files);
            e.target.value = '';
          }}
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
          <CloudUploadIcon
            sx={{
              fontSize: 36,
              color: newEntries.length > 0 ? consoleColors.accent : consoleColors.textSecondary,
            }}
          />
          <Button
            variant="outlined"
            size="small"
            disabled={disabled}
            onClick={(e) => {
              e.stopPropagation();
              fileInputRef.current?.click();
            }}
            sx={{ borderColor: panelBorder, color: isDark ? '#fff' : NAUTILUS.deepOcean }}
          >
            Add an attachment
          </Button>
          <Typography variant="caption" sx={{ color: consoleColors.textSecondary }}>
            Drag & drop files here, or use the button above
          </Typography>
        </Box>
      </Paper>

      {loadingExisting ? (
        <Typography variant="body2" sx={{ color: consoleColors.textSecondary, mb: 1 }}>
          Loading files…
        </Typography>
      ) : null}

      {!hasRows && !loadingExisting ? (
        <Typography variant="body2" sx={{ color: consoleColors.textSecondary, fontStyle: 'italic' }}>
          No files added yet.
        </Typography>
      ) : (
        <Table size="small" sx={{ '& .MuiTableCell-root': { borderColor: panelBorder, py: 0.75 } }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, width: '32%' }}>Title</TableCell>
              <TableCell sx={{ fontWeight: 600, width: '22%' }}>Category</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
              <TableCell align="right" sx={{ width: 72 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {existingFiles.map((file) => (
              <TableRow key={`existing-${file.id}`}>
                <TableCell>
                  <Typography
                    variant="body2"
                    sx={{ fontFamily: 'monospace', fontSize: '0.8rem', wordBreak: 'break-all' }}
                  >
                    {file.filename}
                  </Typography>
                </TableCell>
                <TableCell>
                  <FileCategoryChip
                    category={file.category}
                    label={CATEGORY_LABELS[file.category]}
                    sx={{ borderColor: panelBorder, fontSize: '0.7rem' }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ color: consoleColors.textSecondary }}>
                    {file.description || '—'}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  {onDownloadExisting && (
                    <IconButton
                      size="small"
                      disabled={disabled}
                      onClick={() => void onDownloadExisting(file)}
                      aria-label="Download"
                    >
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  )}
                  {onDeleteExisting && (
                    <IconButton
                      size="small"
                      disabled={disabled}
                      onClick={() => void onDeleteExisting(file.id)}
                      aria-label="Delete"
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}

            {existingFiles.length > 0 && newEntries.length > 0 && (
              <TableRow>
                <TableCell colSpan={4} sx={{ py: 0.5, border: 'none' }}>
                  <Box sx={{ borderTop: `1px dashed ${panelBorder}`, my: 0.5 }} />
                </TableCell>
              </TableRow>
            )}

            {newEntries.map((entry) => (
              <TableRow key={entry.tempId}>
                <TableCell>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: 'monospace',
                      fontSize: '0.8rem',
                      wordBreak: 'break-all',
                      color: entry.sizeError ? 'error.main' : 'inherit',
                    }}
                  >
                    {entry.file.name}
                  </Typography>
                  {entry.sizeError && (
                    <Typography variant="caption" color="error">
                      {entry.sizeError}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Select
                    size="small"
                    fullWidth
                    disabled={disabled}
                    value={entry.category}
                    onChange={(e) =>
                      updateDraft(entry.tempId, { category: e.target.value as AuxCategory })
                    }
                    sx={{ fontSize: '0.8rem' }}
                  >
                    {(Object.keys(CATEGORY_LABELS) as AuxCategory[]).map((cat) => (
                      <MenuItem key={cat} value={cat}>
                        {CATEGORY_LABELS[cat]}
                      </MenuItem>
                    ))}
                  </Select>
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    fullWidth
                    disabled={disabled}
                    placeholder="Optional description"
                    value={entry.description}
                    onChange={(e) => updateDraft(entry.tempId, { description: e.target.value })}
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    disabled={disabled}
                    onClick={() => removeDraft(entry.tempId)}
                    aria-label="Remove"
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
};

export default AuxiliaryFilesWidget;
